// SEOHead.jsx - Dynamic meta tags and structured data for SEO pages
import { useEffect } from 'react';

export default function SEOHead({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage = 'https://proirp.com/og-default.png',
  type = 'article',
  publishedTime,
  modifiedTime,
  author = 'Scott Fluegel',
  faqItems = [] // Array of {question, answer} for FAQ schema
}) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Helper to set or create meta tag
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    setMeta('description', description);
    if (keywords.length > 0) {
      setMeta('keywords', keywords.join(', '));
    }
    setMeta('author', author);

    // Open Graph
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', type, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:site_name', 'Pro IRP', true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Article specific
    if (type === 'article') {
      if (publishedTime) {
        setMeta('article:published_time', publishedTime, true);
      }
      if (modifiedTime) {
        setMeta('article:modified_time', modifiedTime, true);
      }
      setMeta('article:author', author, true);
    }

    // Canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    // Structured Data - Article
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      author: {
        '@type': 'Person',
        name: author,
        url: 'https://proirp.com'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Pro IRP',
        logo: {
          '@type': 'ImageObject',
          url: 'https://proirp.com/logo.png'
        }
      },
      datePublished: publishedTime,
      dateModified: modifiedTime || publishedTime,
      mainEntityOfPage: canonicalUrl
    };

    // Add article schema
    let articleScript = document.querySelector('script[data-schema="article"]');
    if (!articleScript) {
      articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-schema', 'article');
      document.head.appendChild(articleScript);
    }
    articleScript.textContent = JSON.stringify(articleSchema);

    // FAQ Schema if provided
    if (faqItems.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      };

      let faqScript = document.querySelector('script[data-schema="faq"]');
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.setAttribute('data-schema', 'faq');
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify(faqSchema);
    }

    // Cleanup on unmount
    return () => {
      document.title = 'Pro IRP - Medicare Client Retention Platform';
      // Remove schema scripts
      document.querySelector('script[data-schema="article"]')?.remove();
      document.querySelector('script[data-schema="faq"]')?.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, type, publishedTime, modifiedTime, author, faqItems]);

  return null;
}
